import { Table,Tag } from "antd";
import {
  getJobDriveInvitationsApi,
  getJobListApi,
  sendJobDriveInvitationsApi
} from "api/services/jobs";
import Breadcrumbs from "components/Breadcrumbs";
import FilterInput from "components/Form/filterInput";
import { SkeltonLoader } from "components/skeltonLoader";
import { format,parseISO } from "date-fns";
import { useEffect,useMemo,useState } from "react";
import { useSelector } from "react-redux";
import { Link,Navigate } from "react-router-dom";
import Select from "react-select";
import { Bounce,toast } from "react-toastify";
import { customStyleSelect } from "utils/common";
import {
  itemRender,
  pageSizeOptions,
} from "utils/table";

const JobDriveInvitations = () => {
  const { user, userLoading } = useSelector((state) => state.user);
  
  // Check if user has job_drive_invitation permission
  const hasJobDrivePermission = useMemo(() => {
    // Wait until user is fully loaded
    if (userLoading) return null; // null means "still checking"
    // Check if user object is empty (initial state is {})
    if (!user || Object.keys(user).length === 0 || !user.id) return null; // Still waiting for user data
    // If user has no role or modules, they don't have permission
    if (!user?.role?.permissions && !user?.modules) return false;
    // Check for job_drive_invitation permission
    return user.role?.permissions?.some((p) => p.code === "job_drive_invitation") ||
           user?.modules?.some?.((m) => m.code === "job_drive_invitation");
  }, [user, userLoading]);

  const [loading, setLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSettings, setPageSettings] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
  });

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const response = await getJobListApi({ job_drive_enabled: true });
      setJobs(response.data?.results || []);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load jobs", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setJobsLoading(false);
    }
  };

  const fetchInvitations = async (page = 1, pageSize = 50, search = "", jobId = null) => {
    setLoading(true);
    try {
      const params = { 
        page,
        size: pageSize
      };
      
      if (jobId) {
        params.job_id = jobId;
      }
      
      // Add search parameter if provided
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      const response = await getJobDriveInvitationsApi(params);
      // Handle different response structures
      const invitationsData = response.data;
      let invitationsArray = [];
      if (Array.isArray(invitationsData)) {
        invitationsArray = invitationsData;
      } else if (invitationsData?.results && Array.isArray(invitationsData.results)) {
        invitationsArray = invitationsData.results;
      } else if (invitationsData?.invitations && Array.isArray(invitationsData.invitations)) {
        invitationsArray = invitationsData.invitations;
      }
      setInvitations(invitationsArray);
      
      // Update pagination
      if (invitationsData?.count !== undefined) {
        setPageSettings(prev => ({
          ...prev,
          total: invitationsData.count,
          page,
          pageSize
        }));
      } else if (invitationsData?.total !== undefined) {
        setPageSettings(prev => ({
          ...prev,
          total: invitationsData.total,
          page,
          pageSize
        }));
      }
    } catch (error) {
      console.error("Error fetching invitations:", error);
      setInvitations([]);
      toast.error("Failed to load invitations", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasJobDrivePermission === true) {
      fetchJobs();
    }
  }, [hasJobDrivePermission]);

  useEffect(() => {
    if (hasJobDrivePermission === true) {
      fetchInvitations(pageSettings.page, pageSettings.pageSize, searchTerm, selectedJob?.value || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasJobDrivePermission, selectedJob, pageSettings.page, pageSettings.pageSize]);

  // Debounced search effect
  useEffect(() => {
    if (hasJobDrivePermission === true) {
      const timeoutId = setTimeout(() => {
        setPageSettings(prev => ({
          ...prev,
          page: 1
        }));
        fetchInvitations(1, pageSettings.pageSize, searchTerm, selectedJob?.value || null);
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleTableChange = (pagination) => {
    fetchInvitations(pagination.current, pagination.pageSize, searchTerm, selectedJob?.value || null);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleSendInvitation = async (applicantId, slotId) => {
    try {
      await sendJobDriveInvitationsApi([applicantId], slotId);
      
      // Update local state to reflect the invitation was sent
      setInvitations(prevInvitations => 
        prevInvitations.map(invitation => {
          if (invitation.applicant?.id === applicantId && invitation.slot?.id === slotId) {
            return {
              ...invitation,
              status: "sent"
            };
          }
          return invitation;
        })
      );
      
      toast.success("Invitation sent successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || "Failed to send invitation";
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  // Wait for user to load before checking permissions
  if (userLoading || hasJobDrivePermission === null) {
    return <div>Loading...</div>;
  }

  // Redirect if no permission (after hooks)
  if (hasJobDrivePermission === false) {
    return <Navigate to="/manage-jobs" replace />;
  }

  // Prepare job options
  const jobOptions = jobs?.map(job => ({
    label: job.title,
    value: job.id
  })) || [];

  const invitationColumns = [
    {
      title: "Name",
      render: (_, record) => {
        const applicant = record.applicant;
        return `${applicant?.first_name || ""} ${applicant?.last_name || ""}`.trim() || "-";
      },
    },
    {
      title: "Email",
      render: (_, record) => record.applicant?.email || record.email_to || "-",
    },
    {
      title: "Phone",
      render: (_, record) => record.applicant?.phone || record.applicant?.mobile || "-",
    },
    {
      title: "Job",
      render: (_, record) => {
        return record.applicant?.job?.title || "-";
      },
    },
    {
      title: "Time Slot",
      render: (_, record) => {
        if (record.slot) {
          return record.slot.title || "-";
        }
        return <Tag color="default">Not Assigned</Tag>;
      },
    },
    {
      title: "Status",
      render: (_, record) => {
        const status = record.status;
        if (status === "checked_in") {
          const timeString = record.checked_in_at 
            ? format(parseISO(record.checked_in_at), "dd-MM-yyyy hh:mm a")
            : "Checked In";
          return <Tag color="success" title={timeString}>Checked In</Tag>;
        } else if (status === "sent") {
          return <Tag color="processing">Sent</Tag>;
        } else if (status === "cancelled") {
          return <Tag color="error">Cancelled</Tag>;
        } else if (status === "no_show") {
          return <Tag color="warning">No Show</Tag>;
        } else if (status === "created") {
          return <Tag color="default">Created</Tag>;
        }
        return <Tag color="default">Pending</Tag>;
      },
    },
    {
      title: "Action",
      render: (_, record) => (
        <div className="dropdown dropdown-action text-end">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            {record.slot && record.status !== "sent" && (
              <Link
                to="#"
                className="dropdown-item"
                onClick={(e) => {
                  e.preventDefault();
                  handleSendInvitation(record.applicant?.id, record.slot.id);
                }}
              >
                <i className="fa fa-paper-plane m-r-5" /> Send Invitation
              </Link>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle="Job Drive Invitations"
          title="Invitations"
          subtitle="Job Drive"
          hideGrid={true}
        />
        
        <div className="row mb-4">
          <div className="col-lg-4 col-md-12">
            <FilterInput  
              defaultValue={searchTerm} 
              onChange={handleSearchChange} 
              id="search"
              label="Search by name, email, or phone"
            />
          </div>
          <div className="col-lg-4 col-md-12">
            <div className="input-block mb-3">
              {/* <label className="col-form-label">Filter by Job</label> */}
              <Select
                placeholder="Select a job"
                value={selectedJob}
                onChange={(option) => {
                  setSelectedJob(option);
                  setPageSettings(prev => ({ ...prev, page: 1 }));
                }}
                options={jobOptions}
                styles={customStyleSelect}
                isClearable
                isLoading={jobsLoading}
              />
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm" style={{ borderRadius: "12px", border: "1px solid #e0e0e0", overflow: "hidden" }}>
              <style>{`
                .modern-table .ant-table-tbody > tr {
                  background-color: #ffffff !important;
                }
                .modern-table .ant-table-tbody > tr:hover > td {
                  background-color: #f5f5f5 !important;
                }
                .modern-table .ant-table-thead > tr > th {
                  background-color: #f8f9fa !important;
                  font-weight: 600;
                  color: #333;
                  border-bottom: 2px solid #e0e0e0;
                  padding: 10px 12px;
                  font-size: 13px;
                }
                .modern-table .ant-table-tbody > tr > td {
                  border-bottom: 1px solid #f0f0f0;
                  padding: 10px 12px;
                  font-size: 13px;
                }
                .modern-table .ant-table-wrapper .ant-table-pagination.ant-pagination {
                  border-top: 1px solid #f0f0f0;
                  background-color: #ffffff;
                  border-bottom-left-radius: 12px;
                  border-bottom-right-radius: 12px;
                }
                .modern-table .ant-table-tbody > tr > td:last-child {
                  position: relative !important;
                  overflow: visible !important;
                }
                .modern-table .dropdown-action {
                  position: relative !important;
                }
                .modern-table .dropdown-menu {
                  z-index: 9999 !important;
                  position: absolute !important;
                  top: 100% !important;
                  right: 0 !important;
                  left: auto !important;
                  margin-top: 0.125rem !important;
                  min-width: 160px;
                }
                .modern-table .ant-table-wrapper,
                .modern-table .ant-table-body,
                .modern-table .ant-table-container,
                .modern-table .ant-table {
                  overflow: visible !important;
                }
              `}</style>
              {loading ? (
                <SkeltonLoader number={5} active={true} />
              ) : (
                <Table
                  className="modern-table"
                  columns={invitationColumns}
                  dataSource={Array.isArray(invitations) ? invitations : []}
                  rowKey="id"
                  pagination={{
                    total: pageSettings?.total,
                    pageSize: pageSettings?.pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: pageSizeOptions,
                    itemRender: itemRender,
                    position: "right",
                    current: pageSettings?.page,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                    style: { padding: "16px" }
                  }}
                  onChange={handleTableChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDriveInvitations;

